import React, { Fragment, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import { toastr } from "react-redux-toastr";
import api from "../../../../api";
import { AutoCompleteInput } from "../../../../components/form-group";
import ErrorMessage from "../../../../components/text/ErrorMessage";

import { useLeadActivities } from "../../../../core/hooks/useLeadActivities";
import { combineDateAndTime } from "../../../../utils/CombineDateAndTime";
import { ActivityModel } from "./add-activity";
import {
  CustomTimePeriod,
  isTimePeriodValid,
  ITimePeriodData,
} from "../../../../core/components/time-period/custom-time-period";
import {
  createStartOfDayWithTimeZone,
  VehicleModelMinimal,
  VehicleVariantMinimal,
} from "../../../../core";

interface ITimeReservationFormProps {
  leadId?: string;
  activity?: TimeReservationModel;
  onCancel?: () => void;
  onDateChange?: (date: Date) => void;
  onTimePeriodChange?: (data?: ITimePeriodData) => void;
  alwaysShowform?: boolean;
  onSuccess?: () => void;
  onChangeAssignee?: (id: string) => void;
  onEditSubmit?: (activity: ActivityModel) => void;
  isEdit?: boolean;
}

export const TimeReservationForm: React.FunctionComponent<ITimeReservationFormProps> = (
  props
) => {
  const activity = props.activity;
  const {
    control,
    handleSubmit,
    errors,
    watch,
    setValue,
  } = useForm<TimeReservationModel>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });
  const isActivityEditing = props.isEdit ?? false;
  const { createTimeReservation, updating } = useLeadActivities(api.lead);
  const onSubmit = async (data: TimeReservationModel) => {
    if (!isActivityEditing) {
      const startDate = combineDateAndTime(
        createStartOfDayWithTimeZone(data.timePeriod?.startDate!),
        data.timePeriod?.startTime!
      );
      const endDate = combineDateAndTime(
        createStartOfDayWithTimeZone(
          data.timePeriod?.endDate ?? data.timePeriod?.startDate!
        ),
        data.timePeriod?.endTime!
      );
      const apiModel: TimeReservationAPIModel = {
        startDate,
        endDate,
        note: data.note,
        busyEvent: data.busy,
        assignedPersonId: data.salesPerson?._id!,
      };
      createTimeReservation(apiModel, () => {
        toastr.success("Success", "Time reservation created");
        if (props.onSuccess) {
          props.onSuccess();
        }
      });
    } else {
      if (activity != null) {
        const editedActivity: ActivityModel = {
          activityType: "timeOff",
          timePeriod: data.timePeriod,
          salesPerson: data.salesPerson,
          leadId: props.leadId as any,
          note: data.note as any,
          busyEvent: data.busy,
        };
        if (props.onEditSubmit) {
          props.onEditSubmit(editedActivity);
        }
      }
    }
  };
  return (
    <Fragment>
      <div
        className="tab-pane fade  show active"
        id="activity-all"
        role="tabpanel"
        aria-labelledby="activity-tab"
      >
        <div className="mt-4">
          <form id={"time-reservation-form"} onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Date and Time<span className="text-danger">*</span>
              </label>
              <div key={JSON.stringify(activity?.timePeriod ?? {})}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="timePeriod"
                  defaultValue={activity?.timePeriod}
                  render={({ onChange, value, name }) => (
                    <CustomTimePeriod
                      onChange={(val: ITimePeriodData) => {
                        if (isTimePeriodValid(val)) {
                          onChange(val);
                          if (props.onTimePeriodChange) {
                            props.onTimePeriodChange(val);
                          }
                        } else {
                          onChange(undefined);
                          if (props.onTimePeriodChange) {
                            props.onTimePeriodChange(undefined);
                          }
                        }
                      }}
                      defaultValue={activity?.timePeriod}
                    ></CustomTimePeriod>
                  )}
                />
              </div>
              {errors.timePeriod && (
                <ErrorMessage>Please enter a valid time period</ErrorMessage>
              )}
            </div>
            <div
              key={activity?.salesPerson?._id ?? "" + "time-off-salespereson"}
              className="form-group font-weight-semibold mb-3"
            >
              <label htmlFor="" className="font-sm text-uppercase">
                Users<span className="text-danger">*</span>
              </label>
              <Controller
                control={control}
                rules={{ required: true }}
                name="salesPerson"
                defaultValue={activity?.salesPerson}
                render={({ onChange, value, name }) => (
                  <AutoCompleteInput
                    defaultValue={value}
                    placeholder={"User"}
                    onChange={(currentValue) => {
                      onChange(currentValue);
                      if (props.onChangeAssignee) {
                        props.onChangeAssignee(currentValue._id!);
                      }
                    }}
                    onReset={() => {
                      onChange(undefined);
                      if (props.onChangeAssignee) {
                        props.onChangeAssignee(undefined!);
                      }
                    }}
                    apiUrl="api/user/staffs?hideBlocked=true"
                    valueField={"name"}
                    hideBlocked={true}
                  />
                )}
              />
              {errors.salesPerson && (
                <ErrorMessage>Transaction Manager is required</ErrorMessage>
              )}
            </div>

            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                TYPE
              </label>
              <Controller
                control={control}
                name="busy"
                defaultValue={activity?.busy || true}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    value={value}
                    data={[
                      { label: "Busy", value: true },
                      { label: "Free", value: false },
                    ]}
                    textField={"label"}
                    valueField={"value"}
                    placeholder={"Select Type"}
                    onChange={(val) => {
                      onChange(val.value);
                    }}
                  />
                )}
              />
            </div>
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                REASON<span className="text-danger">*</span>
              </label>
              <Controller
                control={control}
                rules={{ required: true }}
                name="note"
                defaultValue={activity?.note}
                render={({ onChange, value, name }) => (
                  <textarea
                    rows={4}
                    className="form-control rounded-sm font-weight-semibold"
                    value={value}
                    onChange={onChange}
                    placeholder={"Comments"}
                  />
                )}
              />
              {errors.note && <ErrorMessage>Reason is required</ErrorMessage>}
            </div>
          </form>
          <div className="text-right">
            <button
              onClick={() => {
                if (props.onCancel) {
                  props.onCancel();
                }
              }}
              className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
              disabled={updating}
            >
              Cancel
            </button>
            <button
              disabled={updating}
              type="submit"
              form="time-reservation-form"
              className="btn btn-primary py-2 font-weight-semibold font-normal"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export interface TimeReservationModel {
  note?: string;
  busy?: boolean;
  year?: string;
  salesPerson?: { _id: string; name: string };
  timePeriod?: ITimePeriodData;
  vehicleModel?: VehicleModelMinimal;
  vehicleVariant?: VehicleVariantMinimal;
  showroom?: { _id: string; title: string };
  vehicle?: { _id: string; displayName: string };
}

export interface TimeReservationAPIModel {
  startDate: Date;
  endDate: Date;
  note?: string;
  busyEvent?: boolean;
  assignedPersonId?: string;
  vehicleId?: string;
  timeOffType?: string;
}
